import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { logger } from '@/utils/logger'
import useBasketStore, { IBasketItem } from '@/context/BasketStore'
import { Loader, X, XCircle, ThumbsUp, MessageSquare, Undo2, Tag } from 'lucide-react'
import PaymentMethods from './PaymentMethods'
import { IProduct, IEstimatedDispatchDays } from '@/context/ProductDataStore'
import { isPurchasableStatus } from '@/utils/stockUtils'
import useRegionStore from '@/context/RegionStore'
import useCountryDetailsStore from '@/context/CountryDetailsStore'
import {
	fetchKibuCarePlans,
	fetchProductData,
	KibuCareData,
	StripeCheckoutData,
} from '@/services/api'
import { useCurrency } from '@/hooks/useCurrency'
import useDiscountStore from '@/context/DiscountStore'
import useDiscountCapture from '@/hooks/useDiscountCapture'
import { getRegionFromPath } from '@/services/countryService'
import BasketItemRow from './BasketItemRow'
import {
	buildEligibleParentGroups,
	getKibuCarePlanSummary,
} from '@/utils/kibuCare'
import { buildCheckoutItemsFromBasket, mergeBasketProduct } from '@/utils/checkout'
import { addEligibleFreeAddonToBasketItem, getEligibleFreeAddonCandidate } from '@/utils/freeAddon'
import { trackRemoveFromCart } from '@/utils/tracking'
import KibuCareSelector, { KibuCarePlanDisplay } from './KibuCareSelector'
import HandlingFeeInfo from './HandlingFeeInfo'
import DutiesPaidNotice from './DutiesPaidNotice'
import useInfoPanelStore from '@/context/InfoPanelStore'
import { useSavedPartRecovery } from '@/hooks/useSavedPartRecovery'

type BasketProps = {
	className?: string
}

interface RemovedItem extends IBasketItem {
	originalIndex: number;
}

const Basket: React.FC<BasketProps> = ({ className }): JSX.Element => {
	const {
		basketOpen,
		setBasketOpen,
		basketItems,
		removeItemFromBasket,
		itemCount,
		updateBasketItem,
		kibuCareSelection,
		setKibuCareSelection,
		clearKibuCareSelection,
	} = useBasketStore()
	const { chosenRegion, detectedRegion } = useRegionStore()
	const { needsRecovery, isRecovering, recoveryError, retryRecovery } = useSavedPartRecovery(chosenRegion || 'gb')
	const { countryDetails } = useCountryDetailsStore()
	const { symbol: currencySymbol, formatPrice, formatPriceFixed, freeDeliveryThreshold } = useCurrency()
	const { discountCode, calculateDiscountAmount, calculateFreeAddonDiscount, calculateFreeAddonSavings, isAddonFree } = useDiscountStore()
	const navigate = useNavigate()
	const location = useLocation()

	// Capture discount codes from URL
	useDiscountCapture()

	const basketRef = useRef<HTMLDivElement>(null)
	const basketFooterRef = useRef<HTMLDivElement>(null)
	const [fetchingCheckoutLink, setFetchingCheckoutLink] = useState<boolean>(false)
	const [checkoutPrice, setCheckoutPrice] = useState<number>(0)
	const [deliveryCost, setDeliveryCost] = useState<number>(0)
	const [amountToFreeDelivery, setAmountToFreeDelivery] = useState<number>(0)
	const [removedItems, setRemovedItems] = useState<RemovedItem[]>([])
	const [basketFooterHeight, setBasketFooterHeight] = useState<number>(320)
	const [isKibuCareSelectorDismissed, setIsKibuCareSelectorDismissed] = useState(false)
	const [kibuCareData, setKibuCareData] = useState<KibuCareData>({ plans: [], productPricing: [] })
	const [kibuCareLoaded, setKibuCareLoaded] = useState(false)
	const lastBasketRegionRef = useRef<string | null>(null)
	const latestBasketRefreshKeyRef = useRef<string | null>(null)
	const basketRefreshInFlightRef = useRef<string | null>(null)
	const shippingLogKeyRef = useRef<string | null>(null)
	const purchasableBasketItems = useMemo(
		() => basketItems.filter(item => isPurchasableStatus(item.colourWay.stockLimits?.stockStatus, item.colourWay.stockLimits?.availableForSale)),
		[basketItems]
	)
	const unavailableBasketItems = useMemo(
		() => basketItems.filter(item => !isPurchasableStatus(item.colourWay.stockLimits?.stockStatus, item.colourWay.stockLimits?.availableForSale)),
		[basketItems]
	)
	const purchasableItemCount = purchasableBasketItems.length
	const hasPreorderItems = purchasableBasketItems.some(
		item => item.colourWay.stockLimits?.stockStatus === 'PREORDER'
	)
	const selectedKibuCarePlanId = kibuCareSelection?.planId || null
	const eligibleKibuCareParentGroups = useMemo(
		() => buildEligibleParentGroups(purchasableBasketItems, kibuCareData.productPricing),
		[purchasableBasketItems, kibuCareData.productPricing]
	)
	const hasKibuCareOptions = kibuCareData.plans.length > 0 && eligibleKibuCareParentGroups.length > 0
	const kibuCarePlanOptions: KibuCarePlanDisplay[] = useMemo(() => {
		if (!hasKibuCareOptions) {
			return []
		}

		return kibuCareData.plans
			.map((plan) => {
				const summary = getKibuCarePlanSummary({
					planId: plan._id,
					basketItems: purchasableBasketItems,
					productPricing: kibuCareData.productPricing
				})
				if (!summary) {
					return null
				}

				const totalLabel = formatPrice(summary.total, true)
				const perUnitLabel = `For ${summary.quantity} item${summary.quantity === 1 ? '' : 's'}`

				return {
					planId: plan._id,
					title: plan.optionTitle || plan.productName,
					totalLabel,
					perUnitLabel,
					total: summary.total
				}
			})
			.filter((option): option is KibuCarePlanDisplay & { total: number } => Boolean(option))
			.sort((a, b) => {
				if (a.total !== b.total) {
					return a.total - b.total
				}
				return a.title.localeCompare(b.title)
			})
			.map(({ total: _total, ...option }) => option)
	}, [hasKibuCareOptions, kibuCareData.plans, kibuCareData.productPricing, purchasableBasketItems, formatPrice])
	const kibuCareEmptyMessage = useMemo(() => {
		if (!basketItems.length) {
			return null
		}
		if (!kibuCareData.plans.length) {
			return 'Kibu Care is not available for your selected region right now.'
		}
		if (!eligibleKibuCareParentGroups.length) {
			return null
		}
		if (!kibuCarePlanOptions.length) {
			return 'Kibu Care plans are available, but pricing is not configured for these items.'
		}
		return null
	}, [basketItems.length, kibuCareData.plans.length, eligibleKibuCareParentGroups.length, kibuCarePlanOptions.length])
	const selectedKibuCareSummary = useMemo(() => {
		if (!selectedKibuCarePlanId || !hasKibuCareOptions) {
			return null
		}
		return getKibuCarePlanSummary({
			planId: selectedKibuCarePlanId,
			basketItems: purchasableBasketItems,
			productPricing: kibuCareData.productPricing
		})
	}, [selectedKibuCarePlanId, hasKibuCareOptions, purchasableBasketItems, kibuCareData.productPricing])
	const kibuCareTotalMajor = selectedKibuCareSummary ? selectedKibuCareSummary.total / 100 : 0
	const selectedKibuCarePlan = useMemo(
		() => kibuCareData.plans.find(plan => plan._id === selectedKibuCarePlanId) || null,
		[kibuCareData.plans, selectedKibuCarePlanId]
	)
	const kibuCareBasketItem = useMemo<IBasketItem | null>(() => {
		if (!selectedKibuCareSummary || !selectedKibuCarePlan) {
			return null
		}
		const firstImage = [...(selectedKibuCarePlan.images || [])].sort((a, b) => {
			const aOrder = typeof a === 'object' && a ? (a.order || 0) : 0
			const bOrder = typeof b === 'object' && b ? (b.order || 0) : 0
			return aOrder - bOrder
		})[0]
		const imageUrl = typeof firstImage === 'string'
			? firstImage
			: firstImage?.thumbnailUrl || firstImage?.url || selectedKibuCarePlan.heroImage

		return {
			id: 'kibu-care-selected',
			name: 'Kibu Care',
			price: kibuCareTotalMajor,
			colourWay: {
				_id: selectedKibuCarePlan._id,
				productName: selectedKibuCarePlan.productName,
				productTitle: 'Kibu Care',
				description: '',
				productCatalogueId: selectedKibuCarePlan.productCatalogueId,
				optionTitle: selectedKibuCarePlan.optionTitle,
				imageUrl: imageUrl || undefined,
				images: selectedKibuCarePlan.images,
				price: {
					price_id: '',
					unit_amount: kibuCareTotalMajor,
					currency: selectedKibuCareSummary.currency
				},
				productDescription: {
					mobile: '',
					desktop: ''
				}
			},
			extras: [],
			extrasData: []
		}
	}, [selectedKibuCareSummary, selectedKibuCarePlan, kibuCareTotalMajor])
	const basketDispatchEstimate = React.useMemo<IEstimatedDispatchDays | null>(() => {
		const estimates: IEstimatedDispatchDays[] = []
		const units = new Set<string>()

		const collectEstimate = (item: IProduct) => {
			const estimate = item.stockLimits?.estimatedDispatchDays
			if (estimate) {
				estimates.push(estimate)
				if (estimate.unit) {
					units.add(estimate.unit)
				}
			}
		}

		purchasableBasketItems.forEach(item => {
			collectEstimate(item.colourWay)
			item.extras.forEach(collectEstimate)
		})

		if (!estimates.length) {
			return null
		}

		let maxMin: number | undefined
		let maxMax: number | undefined
		estimates.forEach(estimate => {
			if (typeof estimate.min === 'number') {
				maxMin = maxMin === undefined ? estimate.min : Math.max(maxMin, estimate.min)
			}
			if (typeof estimate.max === 'number') {
				maxMax = maxMax === undefined ? estimate.max : Math.max(maxMax, estimate.max)
			}
		})

		if (maxMin === undefined && maxMax === undefined) {
			return null
		}

		const unit = units.size === 1 ? Array.from(units)[0] : estimates.find(estimate => estimate.unit)?.unit

		return { min: maxMin, max: maxMax, unit }
	}, [purchasableBasketItems])

	const formatDispatchEstimate = (estimate?: IEstimatedDispatchDays | null): string | null => {
		if (!estimate) {
			return null
		}

		const { min, max, unit } = estimate
		const hasMin = typeof min === 'number'
		const hasMax = typeof max === 'number'
		if (!hasMin && !hasMax) {
			return null
		}

		const unitLabel = unit ? unit.replace(/_/g, ' ') : 'days'
		if (hasMin && hasMax && min !== max) {
			return `${min}-${max} ${unitLabel}`
		}
		const value = hasMin ? min : max
		return `${value} ${unitLabel}`
	}

	const basketDispatchLabel = formatDispatchEstimate(basketDispatchEstimate)

	const isSameBasketProduct = (current: IProduct, next: IProduct): boolean => {
		const currentOptionsCount = current.availableOptions?.length || 0
		const nextOptionsCount = next.availableOptions?.length || 0
		return (
			current._id === next._id &&
			current.price?.unit_amount === next.price?.unit_amount &&
			current.stockLimits?.stockStatus === next.stockLimits?.stockStatus &&
			current.stockLimits?.remainingStock === next.stockLimits?.remainingStock &&
			currentOptionsCount === nextOptionsCount
		)
	}

	useEffect(() => {
		const region = (chosenRegion || 'gb').toLowerCase()
		if (needsRecovery) return
		if (lastBasketRegionRef.current === region) {
			return
		}
		if (!basketItems.length) {
			lastBasketRegionRef.current = region
			return
		}

		const requestKey = `${region}|${basketItems.map(item => item.colourWay._id).join(',')}`
		if (basketRefreshInFlightRef.current === requestKey) {
			return
		}

		lastBasketRegionRef.current = region
		latestBasketRefreshKeyRef.current = requestKey
		basketRefreshInFlightRef.current = requestKey

		const refreshBasketItems = async () => {
			const productCache = new Map<string, Promise<any>>()

			await Promise.all(basketItems.map(async (item) => {
				// Refresh the purchased item, including standalone replacement parts.
				// A catalogue response is display metadata and must never replace its ID.
				const productRequestId = item.colourWay._id
				if (!productCache.has(productRequestId)) {
					productCache.set(productRequestId, fetchProductData(productRequestId, region))
				}

				let productData: any
				try {
					productData = await productCache.get(productRequestId)
				} catch (error) {
					logger.warn('component', 'Basket: Failed to refresh item for region change', { productRequestId, region, error })
					return
				}

				if (latestBasketRefreshKeyRef.current !== requestKey) {
					return
				}

				const updatedProduct = productData?.product as IProduct | undefined
				if (!updatedProduct) {
					return
				}

				const availableOptions = updatedProduct.availableOptions || []
				const optionProducts: IProduct[] = availableOptions.flatMap(option => option.options || [])
				const matchedColourWay = optionProducts.find(optionItem => optionItem._id === item.colourWay._id)

				const updatedColourWay = {
					...mergeBasketProduct(item.colourWay, matchedColourWay || updatedProduct),
					availableOptions: updatedProduct.availableOptions ?? item.colourWay.availableOptions
				}
				const updatedExtras = item.extras.map(extra => {
					const matched = optionProducts.find(optionItem => optionItem._id === extra._id)
					return mergeBasketProduct(extra, matched)
				})

				const colourWayChanged = !isSameBasketProduct(item.colourWay, updatedColourWay)
				const extrasChanged = item.extras.length !== updatedExtras.length || item.extras.some((extra, index) => {
					const nextExtra = updatedExtras[index]
					return !nextExtra || !isSameBasketProduct(extra, nextExtra)
				})

				if (colourWayChanged || extrasChanged) {
					updateBasketItem(item.id, { colourWay: updatedColourWay, extras: updatedExtras })
				}
			}))

			if (latestBasketRefreshKeyRef.current === requestKey) {
				basketRefreshInFlightRef.current = null
			}
		}

		refreshBasketItems()
	}, [basketItems, chosenRegion, updateBasketItem, needsRecovery])

	useEffect(() => {
		let isActive = true
		const countryCode = (chosenRegion || detectedRegion || 'gb').toUpperCase()
		setKibuCareLoaded(false)

		fetchKibuCarePlans(countryCode)
			.then((data) => {
				if (!isActive) {
					return
				}
				setKibuCareData(data)
				setKibuCareLoaded(true)
			})
			.catch(() => {
				if (!isActive) {
					return
				}
				setKibuCareData({ plans: [], productPricing: [] })
				setKibuCareLoaded(true)
			})

		return () => {
			isActive = false
		}
	}, [chosenRegion, detectedRegion])

	useEffect(() => {
		if (!basketOpen) {
			setIsKibuCareSelectorDismissed(false)
		}
	}, [basketOpen])

	useEffect(() => {
		const footerEl = basketFooterRef.current
		if (!footerEl) {
			return
		}

		const updateFooterHeight = () => {
			const nextHeight = Math.ceil(footerEl.getBoundingClientRect().height)
			setBasketFooterHeight((prevHeight) => (
				prevHeight !== nextHeight ? nextHeight : prevHeight
			))
		}

		updateFooterHeight()

		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', updateFooterHeight)
			return () => window.removeEventListener('resize', updateFooterHeight)
		}

		const observer = new ResizeObserver(() => {
			updateFooterHeight()
		})
		observer.observe(footerEl)

		return () => {
			observer.disconnect()
		}
	}, [basketOpen, itemCount, selectedKibuCarePlanId, kibuCareLoaded, kibuCarePlanOptions.length, kibuCareEmptyMessage, amountToFreeDelivery, freeDeliveryThreshold, basketDispatchLabel, isKibuCareSelectorDismissed])

	useEffect(() => {
		if (!basketItems.length) {
			if (kibuCareSelection) {
				clearKibuCareSelection()
			}
			return
		}

		if (!kibuCareSelection) {
			return
		}

		// Avoid clearing persisted selection before plans have loaded.
		if (!kibuCareLoaded) {
			return
		}

		// Only clear if we have real plan data and the selected plan no longer exists.
		if (!kibuCareData.plans.length) {
			return
		}

		const selectedPlanStillValid = kibuCareData.plans.some((plan) => plan._id === kibuCareSelection.planId)
		if (!selectedPlanStillValid) {
			clearKibuCareSelection()
		}
	}, [basketItems.length, kibuCareSelection, kibuCareLoaded, kibuCareData.plans, clearKibuCareSelection])

	// Handle scroll lock
	useEffect(() => {
		const originalStyle = window.getComputedStyle(document.body).overflow

		if (basketOpen) {
			// Save the current scroll position and lock scrolling
			document.body.style.overflow = 'hidden'
		} else {
			// Restore scrolling
			document.body.style.overflow = originalStyle
		}

		// Cleanup function to restore original scroll state
		return () => {
			document.body.style.overflow = originalStyle
		}
	}, [basketOpen])

	const close = () => {
		setBasketOpen(false)
	}

	const handleRemoveItem = (id: string) => {
		const itemToRemove = basketItems.find(item => item.id === id)
		const itemIndex = basketItems.findIndex(item => item.id === id)
		if (itemToRemove) {
			setRemovedItems(prev => [{ ...itemToRemove, originalIndex: itemIndex }, ...prev])
			const region = chosenRegion || detectedRegion || null
			trackRemoveFromCart(itemToRemove, region)
		}
		removeItemFromBasket(id)
	}

	const handleAddFreeAddon = (basketItemId: string, inputValue?: string) => {
		const targetItem = basketItems.find((item) => item.id === basketItemId)
		if (!targetItem) {
			return
		}

		const updatedItem = addEligibleFreeAddonToBasketItem(targetItem, discountCode, inputValue)
		if (!updatedItem) {
			return
		}

		updateBasketItem(basketItemId, updatedItem)
	}

	const handleRemoveAllItems = () => {
		const itemsWithIndices = basketItems.map((item, index) => ({
			...item,
			originalIndex: index
		}))
		setRemovedItems(prev => [...itemsWithIndices, ...prev])
		basketItems.forEach(item => removeItemFromBasket(item.id))
	}

	const handleUndo = () => {
		if (removedItems.length > 0) {
			// Get the most recently removed item
			const [itemToRestore, ...remainingRemovedItems] = removedItems
			setRemovedItems(remainingRemovedItems)

			// Get all current basket items
			const currentBasketItems = [...basketItems]

			// Find where to insert the restored item
			let insertIndex = itemToRestore.originalIndex

			// Adjust insert index if it's beyond current basket size
			if (insertIndex > currentBasketItems.length) {
				insertIndex = currentBasketItems.length
			}

			// Insert the item at the correct position
			currentBasketItems.splice(insertIndex, 0, itemToRestore)

			// Clear the basket and add all items in the correct order
			basketItems.forEach(item => removeItemFromBasket(item.id))
			currentBasketItems.forEach(item => {
				useBasketStore.getState().addItemToBasket(item)
			})
		}
	}

	useEffect(() => {
		// An info panel opened from the basket sits over it; dismissing the panel should leave the basket open.
		const infoPanelOpen = () => useInfoPanelStore.getState().isOpen

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && basketOpen && !infoPanelOpen()) {
				close()
			}
		}

		const handleClickOutside = (e: MouseEvent) => {
			if (basketOpen && !infoPanelOpen() && basketRef.current && !basketRef.current.contains(e.target as Node)) {
				close()
			}
		}

		if (basketOpen) {
			document.addEventListener('keydown', handleKeyDown)
			document.addEventListener('mousedown', handleClickOutside)
		} else {
			document.removeEventListener('keydown', handleKeyDown)
			document.removeEventListener('mousedown', handleClickOutside)
		}

		// Cleanup function
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [basketOpen])

	useEffect(() => {
		let price: number = 0
		purchasableBasketItems.forEach(item => {
			// Calculate dynamic price based on current discount state
			let itemPrice = item.colourWay.price?.unit_amount ?? 0

			// Add addon prices, checking if they're free
			item.extras.forEach(addon => {
				const isFree = isAddonFree(addon._id, addon.productCatalogueId)
				if (!isFree) {
					itemPrice += addon.price?.unit_amount ?? 0
				}
			})

			price += itemPrice * (item.quantity ?? 1)
		})

		if (checkoutPrice !== price) {
			setCheckoutPrice(price)
		}

		const subtotalWithKibuCare = price + kibuCareTotalMajor

		// Get standard shipping rate (fall back to first threshold-type rate if name doesn't match exactly)
		const standardShipping = countryDetails?.shippingRates?.find(rate => rate.name === 'Standard Shipping')
			?? countryDetails?.shippingRates?.find(rate => rate.type === 'threshold')
		if (standardShipping?.type === 'threshold' && standardShipping.thresholds) {
			// Calculate delivery cost based on subtotal after ALL discounts
			const monetaryDiscountAmount = calculateDiscountAmount(subtotalWithKibuCare)
			const finalSubtotal = subtotalWithKibuCare - monetaryDiscountAmount

			const logKey = [
				subtotalWithKibuCare,
				monetaryDiscountAmount,
				finalSubtotal,
				standardShipping.thresholds.length
			].join('|')
			if (shippingLogKeyRef.current !== logKey) {
				logger.debug('component', 'Basket: Calculating shipping', {
					rawPrice: subtotalWithKibuCare,
					monetaryDiscount: monetaryDiscountAmount,
					finalSubtotal,
					availableThresholds: standardShipping.thresholds.length
				})
			}

			// Find ALL qualifying thresholds (where customer's subtotal meets the minimum)
			// Convert finalSubtotal to pence for comparison with threshold minAmount (which is in pence)
			const finalSubtotalInPence = finalSubtotal * 100
			const qualifyingThresholds = standardShipping.thresholds.filter(threshold => finalSubtotalInPence >= threshold.minAmount)

			if (shippingLogKeyRef.current !== logKey) {
				logger.debug('component', `Basket: Found ${qualifyingThresholds.length} qualifying shipping thresholds`)
			}

			// From qualifying thresholds, find the one with the HIGHEST minAmount (best deal for customer)
			const applicableThreshold = qualifyingThresholds.length > 0
				? qualifyingThresholds.reduce((best, current) =>
					current.minAmount > best.minAmount ? current : best
				)
				: standardShipping.thresholds[standardShipping.thresholds.length - 1]

			if (shippingLogKeyRef.current !== logKey) {
				logger.debug('component', `Basket: Selected shipping threshold - ${applicableThreshold.displayName} (${applicableThreshold.amount})`)
			}

			if (deliveryCost !== applicableThreshold.amount) {
				setDeliveryCost(applicableThreshold.amount)
			}

			// If there's a free shipping threshold (amount: 0), calculate amount needed
			const freeShippingThreshold = standardShipping.thresholds.find(threshold => threshold.amount === 0)
			if (freeShippingThreshold) {
				if (shippingLogKeyRef.current !== logKey) {
					logger.debug('component', `Basket: Free shipping threshold: ${freeShippingThreshold.minAmount}`)
				}
				const adjustedSubtotal = finalSubtotal * 100
				if (shippingLogKeyRef.current !== logKey) {
					logger.debug('component', `Basket: Adjusted subtotal for free shipping: ${adjustedSubtotal}`)
				}

				const nextAmountToFree = adjustedSubtotal >= freeShippingThreshold.minAmount
					? 0
					: freeShippingThreshold.minAmount - adjustedSubtotal
				if (amountToFreeDelivery !== nextAmountToFree) {
					setAmountToFreeDelivery(nextAmountToFree)
				}
			}

			if (shippingLogKeyRef.current !== logKey) {
				shippingLogKeyRef.current = logKey
			}
		}
	}, [purchasableBasketItems, countryDetails, calculateDiscountAmount, discountCode, checkoutPrice, deliveryCost, amountToFreeDelivery, kibuCareTotalMajor])

	useEffect(() => {
		logger.debug('component', `Basket: Amount needed for free delivery: ${amountToFreeDelivery}`)
	}, [amountToFreeDelivery])

	const createStripeCheckoutBody = (): StripeCheckoutData | null => {
		const selectedPlanId = selectedKibuCarePlanId
		const { items: checkoutItems, kibuCareItems } = buildCheckoutItemsFromBasket({
			basketItems,
			selectedKibuCarePlanId: selectedPlanId,
			kibuCareProductPricing: kibuCareData.productPricing
		})
		if (selectedPlanId && !kibuCareItems.length) {
			clearKibuCareSelection()
		}

		// Get the country code from the URL path segments
		const countryCode = chosenRegion ? chosenRegion : detectedRegion ? detectedRegion : 'GB'

		const urlRegion = getRegionFromPath(location.pathname)
		const regionPrefix = urlRegion ? `/${urlRegion}` : ''

		setFetchingCheckoutLink(true)
		setBasketOpen(false)
		navigate(`${regionPrefix}/shop/checkout`)
		setFetchingCheckoutLink(false)

		return {
			countryCode: countryCode.toUpperCase(),
			items: checkoutItems
		}
	}

	const handleStartCheckout = () => {
		if (needsRecovery) return
		createStripeCheckoutBody()
	}

	const subtotalWithKibuCare = checkoutPrice + kibuCareTotalMajor
	const monetaryDiscount = calculateDiscountAmount(subtotalWithKibuCare)
	const dutiesHandlingFee = purchasableItemCount > 0 ? countryDetails?.dutiesHandlingFee : null
	const totalWithDelivery = subtotalWithKibuCare * 100 - monetaryDiscount + deliveryCost + (dutiesHandlingFee?.amount ?? 0)

	return (
		<>
			<div
				ref={basketRef}
				className={`basket fixed top-0 right-0 z-60 h-[100dvh] drop-shadow-basket w-full md:w-[600px] transition-all ${className} ${basketOpen ? 'shadow-basket ease-hypereaseout duration-500' : 'shadow-none duration-300 ease-kibushopdefault'}`}
				style={{ transform: basketOpen ? 'translateX(0)' : 'translateX(100%)' }}
			>
				<div className="relative flex flex-col gap-8 w-full h-full bg-white w-full md:p-6">
					<div className="basket-nav flex items-center justify-between p-6 md:p-0 pb-0">
						<div className='flex gap-3 items-center'>
							<div className="opacity-30">Your basket ({itemCount} items)</div>
							<div className="flex gap-2">
								<button
									onClick={() => handleRemoveAllItems()}
									className="text-black/50 hover:text-black transition-colors"
								>
									Clear
								</button>
								{removedItems.length > 0 && (
									<button
										onClick={handleUndo}
										className="flex items-center gap-1 text-orange hover:text-orange/80 transition-colors"
									>
										<Undo2 size={16} />
										Undo
									</button>
								)}
							</div>
						</div>
						<button onClick={close} aria-label="Close basket" className="bg-orange rounded-md p-2 transition-all duration-1000 ease-hypereaseout hover:scale-110">
							<X
								className={`h-4 w-4 transition-all text-white duration-1000 ease-kibushopdefault ${basketOpen ? 'rotate-0' : 'rotate-90'}`}
								strokeWidth={3}
							/>
						</button>
					</div>

					{itemCount === 0 && (
						<div className="basket-no-items text-black/30 text-center">You haven't added anything to your basket yet</div>
					)}
					{unavailableBasketItems.length > 0 && (
						<div className="mx-6 mb-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
							Some items are unavailable in this region and will not be included in checkout.
						</div>
					)}
					<div
						className="basket-items flex flex-col gap-4 p-6 pt-1 md:p-0 max-h-full overflow-y-auto"
						style={{ paddingBottom: `${basketFooterHeight + 16}px` }}
					>
						{basketItems.map((item: IBasketItem) => {
							const isUnavailable = !isPurchasableStatus(
								item.colourWay.stockLimits?.stockStatus,
								item.colourWay.stockLimits?.availableForSale
							)
							const freeAddonCandidate = getEligibleFreeAddonCandidate(item, discountCode)
							const freeAddonCtaLabel = freeAddonCandidate ? `Add free ${freeAddonCandidate.optionTitle}` : null
							const freeAddonInputMaxLength = freeAddonCandidate?.addon.componentModifications?.engravingText?.maxLength

							return (
								<BasketItemRow
									key={item.id}
									item={item}
									currencySymbol={currencySymbol}
									formatPrice={formatPrice}
									isAddonFree={isAddonFree}
									freeAddonCtaLabel={freeAddonCtaLabel}
									freeAddonInputMaxLength={freeAddonInputMaxLength}
									onAddFreeAddon={handleAddFreeAddon}
									isUnavailable={isUnavailable}
									onRemove={handleRemoveItem}
								/>
							)
						})}
						{kibuCareBasketItem && (
							<BasketItemRow
								key={kibuCareBasketItem.id}
								item={kibuCareBasketItem}
								currencySymbol={currencySymbol}
								formatPrice={formatPrice}
								isAddonFree={isAddonFree}
								showQuantityStepper={false}
								onRemove={clearKibuCareSelection}
							/>
						)}
					</div>
					<div
						ref={basketFooterRef}
						className='fixed bottom-0 right-0 left-0 flex flex-col w-9/10 md:w-[600px] bg-white p-6 pt-4 pb-safe border-t border-black/10 gap-1 z-[100]'
					>
						{!isKibuCareSelectorDismissed
							&& !selectedKibuCareSummary
							&& !(kibuCareSelection && !kibuCareLoaded)
							&& kibuCareData.plans.length > 0
							&& (
								<KibuCareSelector
									className="mb-2"
									plans={kibuCareData.plans}
									planOptions={kibuCarePlanOptions}
									selectedPlanId={selectedKibuCarePlanId}
									onSelectPlan={(planId) => setKibuCareSelection({ planId })}
									onClearPlan={clearKibuCareSelection}
									onDismiss={() => setIsKibuCareSelectorDismissed(true)}
									onNavigateAway={() => setBasketOpen(false)}
									emptyMessage={kibuCareEmptyMessage}
								/>
							)}
						{/* Christmas Delivery Notice */}
						{/* <div className="bg-green/10 border border-green rounded-lg p-3 mb-2">
							<div className="flex items-center gap-2">
								<span className="text-lg">🎄</span>
								<div className="flex-1">
									<div className="font-bold text-green text-sm">Order by Dec 20 for Christmas delivery</div>
									<div className="text-xs text-gray-600">UK mainland only</div>
								</div>
							</div>
						</div> */}
						{/* Cost Summary */}
						{/* Delivery Progress Bar */}
						<div className="delivery-progress flex flex-col gap-2 mb-2">
							{amountToFreeDelivery === 0 ? (
								<div className="flex items-center gap-2 text-green-600">
									<ThumbsUp size={20} />
									<span>You've qualified for free delivery</span>
								</div>
							) : freeDeliveryThreshold !== null ? (
								<div className="flex items-center gap-2 text-gray-600">
									<MessageSquare size={20} />
									<span>You're {formatPrice(amountToFreeDelivery, true)} away from free shipping</span>
								</div>
							) : null}
							{freeDeliveryThreshold !== null && (
								<div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
									<div
										className="h-full bg-[#179D1A] transition-all duration-500 ease-out rounded-full"
										style={{
											width: `${Math.min((((subtotalWithKibuCare - monetaryDiscount) / freeDeliveryThreshold) * 10000), 100)}%`
										}}
									/>
								</div>
							)}
						</div>
						<div className="w-full">
							<div className="flex flex-col gap-1 text-base">

								{/* Free Addon Discount Line - Show if free addons are applied */}
								{discountCode && discountCode.valid && discountCode.discountType === 'free_addon' && calculateFreeAddonSavings(basketItems) > 0 && (
									<div className="flex justify-between items-center">
										<div className="flex items-center gap-2 text-specialYellow">
											<Tag size={16} />
											<span>Free Engraving ({discountCode.code})</span>
										</div>
										<span className="text-green">-{formatPrice(calculateFreeAddonSavings(basketItems), false)}</span>
									</div>
								)}

								{/* Monetary Discount Line - Only show if valid monetary discount */}
								{discountCode && discountCode.valid && (discountCode.discountType === 'percentage' || discountCode.discountType === 'fixed') && (
									<div className="flex justify-between items-center">
										<div className="flex items-center gap-2 text-green-600">
											<Tag size={16} />
											<span>Discount ({discountCode.code})</span>
										</div>
										<span className="text-green">-{formatPrice(monetaryDiscount, true)}</span>
									</div>
								)}

								{/* Invalid discount code indicator */}
								{discountCode && !discountCode.valid && !discountCode.isLoading && (
									<div className="flex justify-between items-center">
										<div className="flex items-center gap-2 text-red-500">
											<XCircle size={16} />
											<span>Invalid code ({discountCode.code})</span>
										</div>
										<span className="text-red-500">£0.00</span>
									</div>
								)}

								{/* Loading discount validation */}
								{discountCode && discountCode.isLoading && (
									<div className="flex justify-between items-center">
										<div className="flex items-center gap-2 text-gray-500">
											<Loader size={16} className="animate-spin" />
											<span>Validating ({discountCode.code})</span>
										</div>
										<span className="text-gray-500">...</span>
									</div>
								)}
								<div className="flex justify-between">
									<span className="text-black/50">Subtotal</span>
									<span>{formatPriceFixed(subtotalWithKibuCare, false)}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-black/50">Delivery</span>
									<span>{amountToFreeDelivery === 0 ? 'FREE' : formatPriceFixed(deliveryCost, true)}</span>
								</div>
								{dutiesHandlingFee && (
									<div className="flex justify-between">
										<span className="flex items-center gap-1 text-black/50">
											{dutiesHandlingFee.label}
											<HandlingFeeInfo label={dutiesHandlingFee.label} formattedAmount={formatPriceFixed(dutiesHandlingFee.amount, true)} />
										</span>
										<span>{formatPriceFixed(dutiesHandlingFee.amount, true)}</span>
									</div>
								)}
								<div className="flex justify-between font-bold pt-2 border-t border-black/10">
									<span>Total</span>
									<span>{formatPriceFixed(totalWithDelivery, true)}</span>
								</div>
							</div>
						</div>
						{dutiesHandlingFee && <DutiesPaidNotice className="mt-3" />}

						{isRecovering &&<p role="status" className="mt-2 text-sm">Restoring your saved replacement parts…</p>}
						{recoveryError && <p role="alert" className="mt-2 text-sm text-red-600">{recoveryError} <button onClick={retryRecovery} className="underline">Retry</button></p>}
						<button
							className={`flex items-center gap-2 p-4 bg-orange justify-center w-full mt-2 rounded-xl font-headline text-white leading-none
								transition-all ease-kibu duration-200 items-center cursor-pointer border border-2 border-transparent
								hover:rounded-2xl hover:shadow-button-hover hover:brightness-105 hover:border-black/10 hover:scale-[1.02]
								${fetchingCheckoutLink && 'opacity-70 cursor-not-allowed'}
								${purchasableItemCount < 1 && 'pointer-events-none opacity-30'}`}
							onClick={handleStartCheckout}
							disabled={purchasableItemCount < 1 || fetchingCheckoutLink || needsRecovery}
						>
							{fetchingCheckoutLink ? (
								<>
									<Loader className="h-5 w-5 animate-spin" />
									Opening checkout...
								</>
							) : (
								hasPreorderItems ? 'Preorder' : 'Checkout'
							)}
						</button>

						<div className='flex justify-center mt-1'><PaymentMethods /></div>
						{basketDispatchLabel && (
							<div className="flex flex-row gap-2 items-center justify-center p-0 rounded-2xl mb-4 -mt-1">
								{/* <Package className={"text-black"} /> */}
								<h4 className="font-body text-base text-black/75">Estimated Dispatch: {basketDispatchLabel}</h4>
							</div>
						)}
					</div>
				</div>
			</div>

		</>
	)
}

export default Basket
